import { Box, FormHelperText } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import type { DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useId } from 'react';
import type { Dayjs } from 'dayjs';
import { StyledInputLabel } from './InputLabel';

type DateRangeFieldConfig = {
  value: Dayjs | null;
  onChange: (date: Dayjs | null) => void;
  label?: React.ReactNode;
  id?: string;
  pickerProps?: Omit<DatePickerProps<Dayjs>, 'value' | 'onChange'>;
};

type DateRangeFieldsProps = {
  start: DateRangeFieldConfig;
  end: DateRangeFieldConfig;
  disabled?: boolean;
  direction?: 'row' | 'column';
  containerSx?: SxProps<Theme>;
  className?: string;
  withLocalizationProvider?: boolean;
  error?: string;
};

export const DateRangeFields = ({
  start,
  end,
  disabled = false,
  direction = 'row',
  containerSx,
  className,
  withLocalizationProvider = true,
  error,
}: DateRangeFieldsProps) => {
  const errorId = useId();

  const errorAriaProps = error
    ? {
        'aria-describedby': errorId,
        'aria-invalid': true as const,
      }
    : {};

  const content = (
    <Box
      className={className}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: 0,
        ...(containerSx ?? {}),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          flexDirection: { xs: 'column', sm: direction },
          gap: { xs: 2, sm: 2.5 },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {start.label != null && start.id && (
            <StyledInputLabel id={`${start.id}-label`} htmlFor={start.id}>
              {start.label}
            </StyledInputLabel>
          )}
          <DatePicker
            disabled={disabled}
            value={start.value}
            onChange={start.onChange}
            {...start.pickerProps}
            slotProps={{
              ...(start.pickerProps?.slotProps ?? {}),
              textField: {
                ...(start.pickerProps?.slotProps?.textField ?? {}),
                id: start.id,
                fullWidth: true,
                error: !!error,
                inputProps: {
                  ...(
                    start.pickerProps?.slotProps?.textField as
                      | { inputProps?: Record<string, unknown> }
                      | undefined
                  )?.inputProps,
                  ...(start.id && start.label != null
                    ? { 'aria-labelledby': `${start.id}-label` }
                    : {}),
                  ...errorAriaProps,
                },
              },
            }}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {end.label != null && end.id && (
            <StyledInputLabel id={`${end.id}-label`} htmlFor={end.id}>
              {end.label}
            </StyledInputLabel>
          )}
          <DatePicker
            disabled={disabled}
            value={end.value}
            onChange={end.onChange}
            {...end.pickerProps}
            slotProps={{
              ...(end.pickerProps?.slotProps ?? {}),
              textField: {
                ...(end.pickerProps?.slotProps?.textField ?? {}),
                id: end.id,
                fullWidth: true,
                error: !!error,
                inputProps: {
                  ...(
                    end.pickerProps?.slotProps?.textField as
                      | { inputProps?: Record<string, unknown> }
                      | undefined
                  )?.inputProps,
                  ...(end.id && end.label != null
                    ? { 'aria-labelledby': `${end.id}-label` }
                    : {}),
                  ...errorAriaProps,
                },
              },
            }}
          />
        </Box>
      </Box>
      {error && (
        <FormHelperText id={errorId} error role="alert" sx={{ mt: 0.5 }}>
          {error}
        </FormHelperText>
      )}
    </Box>
  );

  return withLocalizationProvider ? (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {content}
    </LocalizationProvider>
  ) : (
    content
  );
};
